/**
 * The report itself: one period, read once, drawn in the order a reader needs it.
 *
 * **The order is a decision.** The period comes first, because every figure
 * under it is meaningless without its boundaries and its zone — this is the one
 * screen in the product whose subject *is* a period, and a reader who has the
 * dates wrong has everything wrong. Reach follows, because "how many different
 * people did we message" is the question the screen exists to answer and it is
 * the number this product has already got wrong once. The combined totals come
 * next and stop there — accepted and delivered, no third column. Then each
 * channel in its own words, then the campaigns themselves with the instant that
 * dated each one, so a reader who doubts the bucketing can check it.
 *
 * **Everything this view draws from the report comes from one response**, so
 * the report is one moment. `generatedAt` says which moment, in the period's
 * own zone, and the report says it was computed on read rather than stored:
 * nothing here is a snapshot that could be stale, and nothing here is cached.
 * Two slots hold anything else, both handed in by the page. `ads`, slice
 * 44's Meta ad account card, is a second read, of Meta, and carries its own
 * tag; it sits after the WhatsApp card, where the channels end and before the
 * campaigns list — the ad surfaces are surfaces like those two, and their card
 * does not belong under a list that can run to two hundred rows. `bySurface`,
 * slice 45's comparison, follows it and pairs the two reads only when they
 * cover the same days. Both are drawn in an empty period too: an ad account
 * can spend in a period nothing was sent in, and the comparison then holds the
 * ad tiles alone — never a message tile of zeros.
 *
 * **The period printed is the one the API summed, not the one the picker holds.**
 * `report.range` and `report.rangeLabel` come off the response; the form's state
 * is not consulted. That matters in exactly the case that goes wrong otherwise:
 * a refresh that fails leaves the previous period's figures on screen, and they
 * stay labelled with the previous period rather than acquiring the label of the
 * period somebody just asked for and did not get.
 *
 * **An empty period is a sentence and not a grid of zeros.** When the period
 * holds no campaigns, everything below the boundaries collapses to
 * `NO_CAMPAIGNS_IN_RANGE_NOTE` — because a screen of noughts says *we measured
 * and found nothing*, which is a different and much stronger claim than *nothing
 * went out*. The zeros would all be true; the impression would not be.
 *
 * **A channel this workspace does not send on gets a sentence too, never a
 * zero.** `email` and `whatsapp` are nullable on the response and `null` means
 * "not sent from here" — the distinction `CHANNEL_NOT_SENDING_NOTES` exists for.
 *
 * **There is no combined failure figure anywhere on this page**, and no total
 * that mixes the two channels' failure vocabularies. That is decision 4 of the
 * slice, and it is enforced by the contract rather than by this layout: the
 * cross-channel measure enum has two members, `accepted` and `delivered`, and
 * `notDelivered` cannot even be read off an un-narrowed `ChannelAnalytics`.
 *
 * **Slice 17 shortened the card descriptions and moved no card.** The order
 * above is the decision, and it is unchanged; what changed is that a
 * description now says what its card holds rather than arguing for why it holds
 * it. Two survived at full length on purpose: the email card still lists the
 * five ways a message Amazon took never arrived, and the WhatsApp card still
 * says there is no bounce here and no bounce column. Both are the absence of
 * something a reader is looking for, and an absence is the one thing a card
 * cannot state by rendering it.
 */
import {
  formatInstantInZone,
  type AnalyticsReport,
} from '@wend/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@wend/ui';
import type { ReactNode } from 'react';

import { AnalyticsCampaigns } from './AnalyticsCampaigns';
import {
  AnalyticsOpenPeriodNote,
  AnalyticsRangeStatement,
  ChannelNotSendingNote,
  NoCampaignsNote,
} from './AnalyticsCopy';
import { AnalyticsEmailOutcomes, AnalyticsWhatsAppOutcomes } from './AnalyticsChannelPanel';
import { AnalyticsCrossChannel } from './AnalyticsCrossChannel';
import { AnalyticsReachPanel } from './AnalyticsReach';
import { rangeEndsInFuture } from '../../lib/analytics-display';

export interface AnalyticsReportViewProps {
  report: AnalyticsReport;
  /** False when `Intl` would not name this browser's zone. Surfaced, not hidden. */
  zoneResolved: boolean;
  /** The clock, injectable so a render test is not at the mercy of the calendar. */
  now?: Date;
  /** The Meta ad account card, drawn after the channels. Its own read; see the header. */
  ads?: ReactNode;
  /**
   * Slice 45's comparison across the surfaces, right after the ad card — in an
   * empty period too, where it can only hold ad tiles: a message surface that
   * sent nothing has no tile.
   */
  bySurface?: ReactNode;
}

export function AnalyticsReportView({ report, zoneResolved, now, ads, bySurface }: AnalyticsReportViewProps) {
  const clock = now ?? new Date();
  const empty = report.campaignCount.rows === 0;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>The period</CardTitle>
          <CardDescription>
            Read at {formatInstantInZone(new Date(report.generatedAt), report.range.timezone)} from
            this workspace&rsquo;s own campaigns and delivery events — nothing here is stored.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          <AnalyticsRangeStatement
            range={report.range}
            timezone={report.range.timezone}
            zoneResolved={zoneResolved}
            heading="What these figures cover"
            label={report.rangeLabel}
          />

          {rangeEndsInFuture(report.range, clock) && <AnalyticsOpenPeriodNote />}
        </CardContent>
      </Card>

      {empty ? (
        <>
          <Card>
            <CardHeader>
              {/* Messages: an ad account can have spent in a period nothing was
                  sent in, and its card is drawn right under this one. */}
              <CardTitle>No messages went out</CardTitle>
            </CardHeader>
            <CardContent>
              <NoCampaignsNote />
            </CardContent>
          </Card>
          {ads}

          {bySurface}
        </>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Who was reached</CardTitle>
              <CardDescription>
                Three counts over three different populations: people, distinct addresses and rows.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AnalyticsReachPanel
                recipientReach={report.recipientReach}
                email={report.email}
                whatsapp={report.whatsapp}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Across both channels</CardTitle>
              <CardDescription>
                The two measures that mean the same thing on email and on WhatsApp. There is no
                third.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AnalyticsCrossChannel accepted={report.accepted} delivered={report.delivered} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Email</CardTitle>
              <CardDescription>
                Amazon&rsquo;s vocabulary: accepted, delivered, complained, bounced, and the five
                ways a message it took never arrived.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {report.email === null ? (
                <ChannelNotSendingNote channel="email" />
              ) : (
                <AnalyticsEmailOutcomes analytics={report.email} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>WhatsApp</CardTitle>
              <CardDescription>
                Meta&rsquo;s vocabulary: accepted, delivered, read, failed. There is no bounce here
                and no bounce column — WhatsApp has no such category.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {report.whatsapp === null ? (
                <ChannelNotSendingNote channel="whatsapp" />
              ) : (
                <AnalyticsWhatsAppOutcomes analytics={report.whatsapp} />
              )}
            </CardContent>
          </Card>

          {ads}

          {bySurface}

          <Card>
            <CardHeader>
              <CardTitle>Campaigns in this period</CardTitle>
              <CardDescription>
                Each dated by the instant it was sent — the instant that decided its period.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AnalyticsCampaigns
                campaigns={report.campaigns}
                campaignCount={report.campaignCount}
                range={report.range}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
